import React, { useState, useEffect, useRef } from 'react';
import { Download, Upload, History, Clock, AlertTriangle, Search, RefreshCw } from 'lucide-react';
import { backupApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export const DataBackupManager = () => {
    const { user } = useAuth();
    const [history, setHistory] = useState([]);
    const [autoBackups, setAutoBackups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    
    // Search filters
    const [searchUsername, setSearchUsername] = useState('');
    const [searchAction, setSearchAction] = useState('');
    const [searchDateFrom, setSearchDateFrom] = useState('');
    const [searchDateTo, setSearchDateTo] = useState('');
    
    // Restore confirmation
    const [restoreModalOpen, setRestoreModalOpen] = useState(false);
    const [selectedBackup, setSelectedBackup] = useState(null);
    const [countdown, setCountdown] = useState(5);
    const [canConfirm, setCanConfirm] = useState(false);
    const countdownRef = useRef(null);
    
    const fileInputRef = useRef(null);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (restoreModalOpen) {
            setCountdown(5);
            setCanConfirm(false);
            countdownRef.current = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownRef.current);
                        setCanConfirm(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else {
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
        }
        return () => {
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
        };
    }, [restoreModalOpen]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [historyRes, autoBackupsRes] = await Promise.all([
                backupApi.getHistory({
                    username: searchUsername || undefined,
                    action: searchAction || undefined,
                    date_from: searchDateFrom || undefined,
                    date_to: searchDateTo || undefined,
                }),
                backupApi.getAutoBackups()
            ]);
            setHistory(historyRes.data.items || []);
            setAutoBackups(autoBackupsRes.data.backups || []);
        } catch (error) {
            console.error('Failed to load backup data:', error);
            toast.error('Fehler beim Laden der Backup-Daten');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        if (!user?.discord_id) return;
        setExporting(true);
        try {
            const response = await backupApi.exportAll(user.discord_id, user.username);
            const filename = response.data.filename;
            
            // Download the file
            const downloadUrl = backupApi.downloadBackup(filename);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            toast.success('Export erfolgreich!');
            loadData();
        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Export fehlgeschlagen');
        } finally {
            setExporting(false);
        }
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !user?.discord_id) return;
        
        setImporting(true);
        try {
            await backupApi.importAll(file, user.discord_id, user.username);
            toast.success('Import erfolgreich!');
            loadData();
        } catch (error) {
            console.error('Import failed:', error);
            toast.error('Import fehlgeschlagen');
        } finally {
            setImporting(false);
            e.target.value = '';
        }
    };

    const handleRestoreClick = (backup) => {
        setSelectedBackup(backup);
        setRestoreModalOpen(true);
    };

    const handleRestoreConfirm = async () => {
        if (!selectedBackup || !user?.discord_id || !canConfirm) return;
        
        try {
            await backupApi.restoreAutoBackup(selectedBackup.filename, user.discord_id, user.username);
            toast.success('Wiederherstellung erfolgreich!');
            setRestoreModalOpen(false);
            setSelectedBackup(null);
            loadData();
        } catch (error) {
            console.error('Restore failed:', error);
            toast.error('Wiederherstellung fehlgeschlagen');
        }
    };

    const handleSearch = () => {
        loadData();
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getActionBadge = (action) => {
        switch (action) {
            case 'export':
                return <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400">Export</span>;
            case 'import':
                return <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400">Import</span>;
            case 'restore':
                return <span className="px-2 py-1 rounded text-xs font-medium bg-amber-500/20 text-amber-400">Restore</span>;
            default:
                return <span className="px-2 py-1 rounded text-xs font-medium bg-gray-500/20 text-gray-400">{action}</span>;
        }
    };

    return (
        <div className="space-y-6" data-testid="data-backup-manager">
            {/* Top Section: Search & Actions */}
            <div className="p-6 rounded-xl bg-card border border-white/10">
                <div className="flex flex-col lg:flex-row gap-6">
                    {/* Search Filters */}
                    <div className="flex-1 space-y-4">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                            <Search size={18} className="text-cyan-400" />
                            Suche & Filter
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <Label className="text-gray-400 text-xs">Nutzername</Label>
                                <Input
                                    data-testid="search-username"
                                    value={searchUsername}
                                    onChange={(e) => setSearchUsername(e.target.value)}
                                    placeholder="Nutzername..."
                                    className="bg-black/40 border-white/10"
                                />
                            </div>
                            <div>
                                <Label className="text-gray-400 text-xs">Aktion</Label>
                                <select
                                    data-testid="search-action"
                                    value={searchAction}
                                    onChange={(e) => setSearchAction(e.target.value)}
                                    className="w-full p-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm"
                                >
                                    <option value="">Alle</option>
                                    <option value="export">Export</option>
                                    <option value="import">Import</option>
                                    <option value="restore">Restore</option>
                                </select>
                            </div>
                            <div>
                                <Label className="text-gray-400 text-xs">Datum von</Label>
                                <Input
                                    data-testid="search-date-from"
                                    type="date"
                                    value={searchDateFrom}
                                    onChange={(e) => setSearchDateFrom(e.target.value)}
                                    className="bg-black/40 border-white/10"
                                />
                            </div>
                            <div>
                                <Label className="text-gray-400 text-xs">Datum bis</Label>
                                <Input
                                    data-testid="search-date-to"
                                    type="date"
                                    value={searchDateTo}
                                    onChange={(e) => setSearchDateTo(e.target.value)}
                                    className="bg-black/40 border-white/10"
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <Button
                                onClick={handleSearch}
                                className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30"
                                data-testid="search-button"
                            >
                                <Search size={16} className="mr-2" />
                                Suchen
                            </Button>
                            <Button
                                onClick={handleExport}
                                disabled={exporting}
                                className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                                data-testid="export-button"
                            >
                                <Download size={16} className="mr-2" />
                                {exporting ? 'Exportiere...' : 'Daten exportieren'}
                            </Button>
                            <Button
                                onClick={handleImportClick}
                                disabled={importing}
                                className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30"
                                data-testid="import-button"
                            >
                                <Upload size={16} className="mr-2" />
                                {importing ? 'Importiere...' : 'Daten importieren'}
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".json"
                                onChange={handleFileSelect}
                                className="hidden"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Section: History & Auto Backups */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* History - 3/5 width */}
                <div className="lg:col-span-3 p-6 rounded-xl bg-card border border-white/10">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                            <History size={18} className="text-purple-400" />
                            Backup History
                        </h3>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={loadData}
                            className="text-gray-400 hover:text-white"
                        >
                            <RefreshCw size={14} />
                        </Button>
                    </div>
                    
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : history.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            Keine History-Einträge gefunden
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {history.map((entry) => (
                                <div
                                    key={entry.id}
                                    className="p-3 rounded-lg bg-white/5 border border-white/10"
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-white">{entry.username}</span>
                                        {getActionBadge(entry.action)}
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-gray-500">
                                        <span>{entry.filename || 'N/A'}</span>
                                        <span>{formatDate(entry.created_at)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Auto Backups - 2/5 width */}
                <div className="lg:col-span-2 p-6 rounded-xl bg-card border border-white/10">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                            <Clock size={18} className="text-amber-400" />
                            Auto-Backups
                        </h3>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={loadData}
                            className="text-gray-400 hover:text-white"
                        >
                            <RefreshCw size={14} />
                        </Button>
                    </div>
                    
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : autoBackups.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            Keine Auto-Backups verfügbar
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {autoBackups.map((backup) => (
                                <div
                                    key={backup.filename}
                                    className="p-2 rounded-lg bg-white/5 border border-white/10 flex items-center justify-between gap-2"
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-white font-mono truncate">
                                            auto_backup
                                        </p>
                                        <span className="text-xs text-gray-500">{formatDate(backup.created_at)}</span>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleRestoreClick(backup)}
                                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 shrink-0 text-xs px-2 py-1 h-7"
                                        data-testid={`restore-${backup.filename}`}
                                    >
                                        Anwenden
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Restore Confirmation Modal */}
            <Dialog open={restoreModalOpen} onOpenChange={setRestoreModalOpen}>
                <DialogContent className="bg-gray-900 border-red-500/30 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-red-400 flex items-center gap-2">
                            <AlertTriangle size={24} />
                            Sicherheitswarnung
                        </DialogTitle>
                        <DialogDescription className="text-gray-400">
                            Diese Aktion überschreibt alle aktuellen Daten!
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                            <p className="text-sm text-red-300">
                                Du bist dabei, das Backup <strong className="font-mono">{selectedBackup?.filename}</strong> wiederherzustellen.
                            </p>
                            <p className="text-sm text-red-300 mt-2">
                                <strong>ACHTUNG:</strong> Alle aktuellen Daten werden unwiderruflich überschrieben!
                            </p>
                        </div>
                        
                        {!canConfirm && (
                            <div className="text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 border-2 border-amber-500/50">
                                    <span className="text-2xl font-bold text-amber-400">{countdown}</span>
                                </div>
                                <p className="text-sm text-gray-400 mt-2">
                                    Bitte warte {countdown} Sekunde{countdown !== 1 ? 'n' : ''}...
                                </p>
                            </div>
                        )}
                    </div>
                    
                    <DialogFooter className="gap-2">
                        <Button
                            variant="ghost"
                            onClick={() => setRestoreModalOpen(false)}
                            className="text-gray-400"
                        >
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleRestoreConfirm}
                            disabled={!canConfirm}
                            className={cn(
                                "transition-all",
                                canConfirm 
                                    ? "bg-red-500 hover:bg-red-600 text-white" 
                                    : "bg-gray-700 text-gray-500 cursor-not-allowed"
                            )}
                            data-testid="confirm-restore-button"
                        >
                            {canConfirm ? 'Ja, wiederherstellen' : 'Warte...'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
